import { Theme } from '@logto/schemas';
import type { TFuncKey } from 'i18next';
import { useContext } from 'react';
import { useSearchParams } from 'react-router-dom';

import StaticPageLayout from '@/Layout/StaticPageLayout';
import PageContext from '@/Providers/PageContextProvider/PageContext';
import EmptyStateDark from '@/assets/icons/empty-state-dark.svg?react';
import EmptyState from '@/assets/icons/empty-state.svg?react';
import DynamicT from '@/components/DynamicT';
import NavBar from '@/components/NavBar';
import PageMeta from '@/components/PageMeta';

import SupportInfo from './SupportInfo';
import styles from './index.module.scss';

type Props = {
  readonly title?: TFuncKey;
  readonly message?: TFuncKey;
  readonly rawMessage?: string;
};

const ErrorPage = ({ title = 'description.not_found', message, rawMessage }: Props) => {
  const { theme } = useContext(PageContext);
  const [searchParams] = useSearchParams();
  
  // Check for specific error messages from query parameters
  const errorParam = searchParams.get('message');
  
  // Determine title and message based on query parameter
  let finalTitle = title;
  let finalMessage = message;
  let finalRawMessage = rawMessage;
  
  if (errorParam === 'session_expired') {
    finalTitle = 'error.invalid_session';
    finalRawMessage = 'Your session has expired or the user ID is invalid. Please start the sign-in process again.';
  } else if (errorParam === 'invalid_session') {
    finalTitle = 'error.invalid_session';
    finalRawMessage = 'Invalid session detected. Please restart the authentication process.';
  }
  
  const errorMessage = Boolean(finalRawMessage ?? finalMessage);

  return (
    <StaticPageLayout>
      <PageMeta titleKey={finalTitle} />
      {history.length > 1 && <NavBar />}
      <div className={styles.container}>
        {theme === Theme.Light ? <EmptyState /> : <EmptyStateDark />}
        <div className={styles.title}>
          <DynamicT forKey={finalTitle} />
        </div>
        {errorMessage && (
          <div className={styles.message}>{finalRawMessage ?? <DynamicT forKey={finalMessage} />}</div>
        )}
        <SupportInfo />
      </div>
    </StaticPageLayout>
  );
};

export default ErrorPage;
